import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Facebook,
  Youtube,
  Instagram,
  ExternalLink,
  Heart,
  MessageCircle,
  Eye,
  Share2,
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  ThumbsUp,
  TrendingUp,
  Clock,
  Maximize2,
  WifiOff,
  Loader2
} from 'lucide-react';
import { cn } from '@utils/cn';
import { fetchAllSocialMediaPosts, checkApiHealth, fetchPostComments, type SocialMediaPost, type SocialComment } from '@services/social-media.service';

type Platform = 'facebook' | 'youtube' | 'instagram' | 'tiktok';

const platformConfig: Record<Platform, { icon: any; color: string; bg: string; label: string; gradient: string }> = {
  facebook: {
    icon: Facebook,
    color: 'text-blue-600',
    bg: 'bg-blue-600',
    label: 'Facebook',
    gradient: 'from-blue-600 to-blue-700'
  },
  youtube: {
    icon: Youtube,
    color: 'text-red-600',
    bg: 'bg-red-600',
    label: 'YouTube',
    gradient: 'from-red-600 to-red-700'
  },
  instagram: {
    icon: Instagram,
    color: 'text-pink-600',
    bg: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    label: 'Instagram',
    gradient: 'from-purple-600 via-pink-600 to-orange-500'
  },
  tiktok: {
    icon: Play,
    color: 'text-gray-900',
    bg: 'bg-gray-900',
    label: 'TikTok',
    gradient: 'from-gray-800 to-gray-900'
  }
};

const formatNumber = (num: number | undefined | null): string => {
  if (num == null || typeof num !== 'number') return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export function SocialMediaGallery() {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | 'all'>('all');
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState<'connected' | 'fallback' | 'error'>('fallback');

  // Fetch real data from APIs
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const health = await checkApiHealth();
        if (health && Object.values(health.platforms).some(v => v)) {
          setApiStatus('connected');
        } else {
          setApiStatus('fallback');
        }

        const data = await fetchAllSocialMediaPosts();
        setPosts(data);
      } catch (error) {
        console.error('Failed to load social media data:', error);
        setApiStatus('error');
        const data = await fetchAllSocialMediaPosts();
        setPosts(data);
      } finally {
        setLoading(false);
      }
    }

    loadData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(loadData, 300000);
    return () => clearInterval(interval);
  }, []);

  const filteredPosts = selectedPlatform === 'all'
    ? posts
    : posts.filter(post => post.platform === selectedPlatform);

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(featuredPosts.length, 1));
  }, [featuredPosts.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + Math.max(featuredPosts.length, 1)) % Math.max(featuredPosts.length, 1));
  }, [featuredPosts.length]);

  useEffect(() => {
    if (featuredPosts.length > 1) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [nextSlide, featuredPosts.length]);

  const openPost = (post: SocialMediaPost) => {
    setSelectedPost(post);
    setShowComments(false);
    setComments([]);
    setCommentsLoading(false);
  };

  const closePost = () => {
    setSelectedPost(null);
    setShowComments(false);
    setComments([]);
    setCommentsLoading(false);
  };

  const handleToggleComments = async () => {
    if (showComments) {
      setShowComments(false);
      return;
    }

    // If we already have comments, just show them
    if (comments.length > 0) {
      setShowComments(true);
      return;
    }

    // Show comments section and start loading
    setShowComments(true);
    setCommentsLoading(true);

    try {
      const postId = selectedPost?.id || '';
      const platform = selectedPost?.platform || '';

      // Only fetch from Facebook API
      if (platform === 'facebook') {
        const fetchedComments = await fetchPostComments(postId, platform);
        setComments(fetchedComments);
      } else {
        // For other platforms, use the comments from the post data
        setComments(selectedPost?.comments || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments(selectedPost?.comments || []);
    } finally {
      setCommentsLoading(false);
    }
  };

  const platforms: (Platform | 'all')[] = ['all', 'facebook', 'youtube', 'instagram', 'tiktok'];

  return (
    <section className="py-16 bg-gradient-to-b from-white via-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-600 to-blue-600 text-white px-4 py-2 rounded-full mb-4">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-semibold uppercase tracking-wider">Social Stories</span>
          </div>
          <p className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
            Dr. Rui in Action
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Follow our journey across social media. See the moments, hear the stories, and join the conversation.
          </p>
          
          {/* API Status Indicator */}
          {apiStatus === 'connected' && (
            <div className="mt-4 inline-flex items-center gap-2 text-green-600 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Live data from social media platforms</span>
            </div>
          )}
          {apiStatus === 'fallback' && (
            <div className="mt-4 inline-flex items-center gap-2 text-yellow-600 text-sm">
              <WifiOff className="w-4 h-4" />
              <span>Showing cached content</span>
            </div>
          )}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-sky-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading social media content...</p>
          </div>
        )}

        {/* Platform Filter */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {platforms.map((platform) => {
              const isActive = selectedPlatform === platform;
              const config = platform !== 'all' ? platformConfig[platform] : null;
              const Icon = config?.icon || TrendingUp;

              return (
                <button
                  key={platform}
                  onClick={() => {
                    setSelectedPlatform(platform);
                    setCurrentSlide(0);
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all',
                    isActive
                      ? 'bg-gradient-to-r from-sky-600 to-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="capitalize">{platform === 'all' ? 'All Platforms' : platform}</span>
                </button>
              );
            })}
          </motion.div>
        )}

        {/* Featured Stories Carousel */}
        {!loading && featuredPosts.length > 0 && selectedPlatform === 'all' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-16"
          >
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-sky-600" />
              Trending Now
            </h3>

            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                    className="relative"
                  >
                    <FeaturedStoryCard
                      post={featuredPosts[currentSlide]}
                      onClick={() => openPost(featuredPosts[currentSlide])}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Arrows */}
              {featuredPosts.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-900" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-900" />
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {featuredPosts.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {featuredPosts.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={cn(
                        'w-3 h-3 rounded-full transition-all',
                        index === currentSlide
                          ? 'bg-sky-600 w-8'
                          : 'bg-gray-300 hover:bg-gray-400'
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Stories Grid */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6">
              {selectedPlatform === 'all' ? 'Latest Stories' : `${platformConfig[selectedPlatform].label} Stories`}
            </h3>

            {regularPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {regularPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <StoryCard
                      post={post}
                      onClick={() => openPost(post)}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <ExternalLink className="w-16 h-16 mx-auto" />
                </div>
                <h4 className="text-xl font-semibold text-gray-700 mb-2">
                  No stories yet
                </h4>
                <p className="text-gray-500">
                  Follow us on social media for updates!
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Post Detail Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={closePost}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <PostDetailModal
                post={selectedPost}
                showComments={showComments}
                comments={comments}
                commentsLoading={commentsLoading}
                onClose={closePost}
                onToggleComments={handleToggleComments}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// Featured Story Card Component
function FeaturedStoryCard({ post, onClick }: { post: SocialMediaPost; onClick: () => void }) {
  const platform = platformConfig[post.platform];
  const PlatformIcon = platform.icon;

  return (
    <div
      className="relative cursor-pointer group"
      onClick={onClick}
    >
      <div className="relative h-[500px] overflow-hidden">
        {/* Background Image or Gradient */}
        {post.thumbnailUrl ? (
          <img
            src={post.thumbnailUrl}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={{ zIndex: 0 }}
            loading="eager"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-sky-400 to-blue-600" />
        )}

        {/* Content Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Platform Badge */}
        <div className="absolute top-6 left-6 z-10">
          <span className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold text-sm',
            `bg-gradient-to-r ${platform.gradient}`
          )}>
            <PlatformIcon className="w-4 h-4" />
            {platform.label}
          </span>
        </div>

        {/* Featured Badge */}
        {post.featured && (
          <div className="absolute top-6 right-6 z-10">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500 text-white font-bold text-xs">
              <TrendingUp className="w-3 h-3" />
              TRENDING
            </span>
          </div>
        )}

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="flex items-center gap-4 mb-4 text-sm">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {formatNumber(post.stats.views)} views
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4" />
              {formatNumber(post.stats.likes)} likes
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              {formatNumber(post.stats.comments)} comments
            </span>
          </div>

          <h3 className="text-3xl md:text-4xl font-heading font-black mb-3 group-hover:text-sky-300 transition-colors">
            {post.title}
          </h3>

          <p className="text-lg text-white/90 mb-4 line-clamp-2">
            {post.description}
          </p>

          <div className="flex items-center gap-2 text-sm text-white/70 mb-6">
            <Clock className="w-4 h-4" />
            <span>{formatDate(post.date)}</span>
          </div>

          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all hover:scale-105">
              <Maximize2 className="w-4 h-4" />
              View Full Story
            </button>
            <a
              href={post.platformUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Open in {platform.label}
            </a>
          </div>
        </div>

        {/* Play Button for Videos */}
        {post.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-gray-900 ml-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Regular Story Card Component
function StoryCard({ post, onClick }: { post: SocialMediaPost; onClick: () => void }) {
  const platform = platformConfig[post.platform];
  const PlatformIcon = platform.icon;

  return (
    <div
      className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all cursor-pointer overflow-hidden border border-gray-100 hover:border-sky-300"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-sky-400 to-blue-600">
        {/* Real Image (if available) */}
        {post.thumbnailUrl && (
          <img
            src={post.thumbnailUrl}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            style={{ zIndex: 0 }}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}

        {/* Dark overlay gradient (only if image exists) */}
        {post.thumbnailUrl && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        )}

        {/* Platform Icon (behind everything) */}
        {!post.thumbnailUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <PlatformIcon className="w-20 h-20 text-white/20" />
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute top-3 left-3 z-10">
          <span className={cn(
            'inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-xs font-bold',
            `bg-gradient-to-r ${platform.gradient}`
          )}>
            {post.type === 'video' ? <Play className="w-3 h-3" /> : post.type === 'story' ? <Clock className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {post.type.toUpperCase()}
          </span>
        </div>

        {/* Stats Badge */}
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-semibold">
            <Eye className="w-3 h-3" />
            {formatNumber(post.stats.views)}
          </span>
        </div>

        {/* Play Button for Videos */}
        {post.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
              <Play className="w-6 h-6 text-gray-900 ml-1" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Platform & Date */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PlatformIcon className={cn('w-4 h-4', platform.color)} />
            <span className="text-xs font-semibold text-gray-600">{platform.label}</span>
          </div>
          <span className="text-xs text-gray-500">{formatDate(post.date)}</span>
        </div>

        {/* Title */}
        <h4 className="font-heading font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
          {post.title}
        </h4>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {post.description}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1 text-gray-600 hover:text-red-600 transition-colors">
              <Heart className="w-4 h-4" />
              <span className="text-xs font-semibold">{formatNumber(post.stats.likes)}</span>
            </button>
            <button className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors">
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs font-semibold">{formatNumber(post.stats.comments)}</span>
            </button>
          </div>
          <button className="text-gray-600 hover:text-sky-600 transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        </div>

        {/* External Link */}
        <a
          href={post.platformUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold text-sm transition-all group/link"
        >
          <span>View on {platform.label}</span>
          <ExternalLink className="w-3 h-3 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </div>
  );
}

// Post Detail Modal Component
function PostDetailModal({
  post,
  showComments,
  comments,
  commentsLoading,
  onClose,
  onToggleComments
}: {
  post: SocialMediaPost;
  showComments: boolean;
  comments: SocialComment[];
  commentsLoading: boolean;
  onClose: () => void;
  onToggleComments: () => void;
}) {
  const platform = platformConfig[post.platform];
  const PlatformIcon = platform.icon;

  return (
    <>
      {/* Modal Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-2xl z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg text-white', `bg-gradient-to-r ${platform.gradient}`)}>
              <PlatformIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{post.author}</h3>
              <p className="text-xs text-gray-500">{formatDate(post.date)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <div className="p-6">
        {/* Media Section */}
        <div className="relative h-64 bg-gradient-to-br from-sky-400 to-blue-600 rounded-xl mb-6 overflow-hidden">
          {post.thumbnailUrl ? (
            <>
              <img
                src={post.thumbnailUrl}
                alt={post.title}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ zIndex: 0 }}
                loading="eager"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlatformIcon className="w-32 h-32 text-white/20" />
            </div>
          )}
          {post.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                <Play className="w-8 h-8 text-gray-900 ml-1" />
              </div>
            </div>
          )}
        </div>

        {/* Title & Description */}
        <h2 className="text-2xl md:text-3xl font-heading font-black text-gray-900 mb-3">
          {post.title}
        </h2>

        <p className="text-gray-700 text-base mb-6 leading-relaxed">
          {post.content}
        </p>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-sky-600 mb-1">
              <Eye className="w-5 h-5" />
            </div>
            <div className="font-bold text-gray-900">{formatNumber(post.stats.views)}</div>
            <div className="text-xs text-gray-500">Views</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
              <ThumbsUp className="w-5 h-5" />
            </div>
            <div className="font-bold text-gray-900">{formatNumber(post.stats.likes)}</div>
            <div className="text-xs text-gray-500">Likes</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="font-bold text-gray-900">{formatNumber(post.stats.comments)}</div>
            <div className="text-xs text-gray-500">Comments</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
              <Share2 className="w-5 h-5" />
            </div>
            <div className="font-bold text-gray-900">{formatNumber(post.stats.shares)}</div>
            <div className="text-xs text-gray-500">Shares</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={onToggleComments}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-lg transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            {showComments ? 'Hide Comments' : `View Comments (${post.stats.comments})`}
          </button>
          <a
            href={post.platformUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-lg transition-all"
          >
            <ExternalLink className="w-5 h-5" />
            Open in {platform.label}
          </a>
        </div>

        {/* Comments Section */}
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-200 pt-6"
          >
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-sky-600" />
              Comments ({comments.length})
            </h4>

            {commentsLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-sky-600 animate-spin mb-3" />
                <p className="text-gray-500">Loading comments...</p>
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment, index) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-3 p-4 bg-gray-50 rounded-lg"
                  >
                    {/* Avatar */}
                    {comment.avatar ? (
                      <img
                        src={comment.avatar}
                        alt={comment.author}
                        className="w-10 h-10 rounded-full flex-shrink-0 object-cover border-2 border-white"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {comment.author.charAt(0).toUpperCase()}
                      </div>
                    )}

                    {/* Comment Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900 text-sm">{comment.author}</span>
                        <span className="text-xs text-gray-500">{formatDate(comment.date)}</span>
                      </div>
                      <p className="text-gray-700 text-sm mb-2">{comment.text}</p>
                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-1 text-gray-500 hover:text-red-600 transition-colors text-xs">
                          <Heart className="w-3 h-3" />
                          {comment.likes}
                        </button>
                        <button className="text-gray-500 hover:text-sky-600 transition-colors text-xs">
                          Reply
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No comments yet. Be the first to comment!</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </>
  );
}
